import { useProfileFilters } from '../store/store';
import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { useFetchWithAuth } from '@/utils/fetch-auth';
import { Download, Loader2 } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle
} from '@/components/ui/dialog';
import { dateToYearMonthDay } from '@contextual/common/utils';

// Child component: Dialog and download logic
function ProfileCsvDownloadDialog({
  open,
  onOpenChange
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const { dateRange } = useProfileFilters();
  const fetchWithAuth = useFetchWithAuth();
  const [isDownloading, setIsDownloading] = useState(false);
  const [confirmChecks, setConfirmChecks] = useState({
    optIn: false,
    responsibility: false
  });

  const downloadCSV = async () => {
    try {
      if (!dateRange) {
        throw new Error('Date range is required for CSV download');
      }
      setIsDownloading(true);

      const csvUrl = new URL(`${import.meta.env.VITE_CONTEXTUAL_API_URL}/profiles/csv`);
      csvUrl.searchParams.append('startDate', dateRange.startDate.toISOString());
      csvUrl.searchParams.append('endDate', dateRange.endDate.toISOString());

      const response = await fetchWithAuth(csvUrl.toString());
      if (!response.ok) {
        throw new Error('Failed to download CSV');
      }

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `profiles_${dateToYearMonthDay(dateRange.startDate)}_to_${dateToYearMonthDay(dateRange.endDate)}.csv`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);

      // Close dialog and reset state after successful download
      onOpenChange(false);
      setIsDownloading(false);
      setConfirmChecks({ optIn: false, responsibility: false });
    } catch (error) {
      console.error('Error downloading CSV:', error);
      setIsDownloading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={isDownloading ? undefined : onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Export CSV</DialogTitle>
        </DialogHeader>
        <div className="space-y-2 py-2 px-4">
          <label className="flex items-start gap-2">
            <input
              type="checkbox"
              checked={confirmChecks.optIn}
              onChange={(e) => {
                setConfirmChecks((prev) => ({ ...prev, optIn: e.target.checked }));
              }}
              className="mt-1 accent-primary"
            />
            <span>
              I understand that this download will only include email addresses who have opted in.
            </span>
          </label>
          <label className="flex items-start gap-2">
            <input
              type="checkbox"
              checked={confirmChecks.responsibility}
              onChange={(e) => {
                setConfirmChecks((prev) => ({ ...prev, responsibility: e.target.checked }));
              }}
              className="mt-1 accent-primary"
            />
            <span>
              I understand that I take responsibility for how phone numbers are used and that they
              should be used only in accordance with individual state&apos;s laws and regulations.
            </span>
          </label>
        </div>
        {isDownloading && (
          <div className="px-4 py-2">
            <div className="flex items-center gap-2 text-sm text-muted-foreground">
              <Loader2 className="h-4 w-4 animate-spin" />
              <span>Preparing your download...</span>
            </div>
          </div>
        )}
        <DialogFooter>
          <Button
            variant="outline"
            onClick={() => {
              onOpenChange(false);
            }}
            disabled={isDownloading}
          >
            Cancel
          </Button>
          <Button
            variant="default"
            className="bg-[#334155] text-[#D9F99D] hover:bg-[#334155]"
            onClick={() => {
              void downloadCSV();
            }}
            disabled={isDownloading || !confirmChecks.optIn || !confirmChecks.responsibility}
          >
            {isDownloading ? (
              <span className="flex items-center gap-2">
                <Loader2 className="h-4 w-4 animate-spin" />
                Downloading...
              </span>
            ) : (
              <span className="flex items-center gap-2">
                <Download className="h-4 w-4" />
                Export CSV
              </span>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// Parent component: manages dialog open/close
export function ProfileCsvDownload() {
  const [openConfirmDialog, setOpenConfirmDialog] = useState(false);

  return (
    <>
      <Button
        variant="outline"
        onClick={() => {
          setOpenConfirmDialog(true);
        }}
      >
        <Download height={16} />
        Export CSV
      </Button>
      <ProfileCsvDownloadDialog open={openConfirmDialog} onOpenChange={setOpenConfirmDialog} />
    </>
  );
}
